/**
 * Home — root route ("/").
 *
 * Renders either the new Penpot-matched dashboard or the legacy line-chart
 * dashboard. Selection is controlled by `useDashboardVersion`: the toggle is
 * available to every company in every environment, and which view opens first
 * depends only on the environment (prod starts on the legacy dashboard,
 * everywhere else on the new one) until the user makes their own choice.
 *
 * The `Sidebar` named export is consumed by the global layout — preserved
 * verbatim from the legacy implementation.
 */

import * as React from "react";
import { Button } from "components/button";
import { useTranslation } from "react-i18next";
import { useModalContext } from "components/modal";
import { RiAddFill } from "react-icons/ri";
import {
  SidebarCon,
  MenuLink,
  MenuContainer,
  SideBarSection,
} from "./style";
import CreateModal from "./components/createModal";
import { getQboTokenInfo, pingQboCompany } from "services/reconciliationService";
import Dashboard from "./dashboard/Dashboard";
import OldDashboard from "./OldDashboard";
import DashboardVersionToggle from "./DashboardVersionToggle";
import { useDashboardVersion } from "./dashboardVersion";

export default function Home() {
  const { version, setVersion } = useDashboardVersion();
  return (
    <>
      {/* Header row: dashboard-version toggle, right-aligned. Announcement
          banners now live inside the new dashboard, between the greeting and
          the widget grid (see DashboardBanners). */}
      <div className="px-4 pt-4 flex items-start justify-end gap-3">
        <DashboardVersionToggle value={version} onChange={setVersion} />
      </div>
      {version === "new" ? <Dashboard /> : <OldDashboard />}
    </>
  );
}

export const Sidebar = () => {
  const { t } = useTranslation();
  const tBase = "views.home.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { setModal } = useModalContext();

  const preflightQboConnection = React.useCallback(async () => {
    try {
      await getQboTokenInfo();
      await pingQboCompany();
    } catch (e) {
      // Do not block navigation; failures are handled on the QBO page.
      // eslint-disable-next-line no-console
      console.debug("QBO preflight ping failed", e);
    }
  }, []);

  return (
    <SidebarCon>
      <SideBarSection>
        <Button
          onClick={() =>
            setModal({ component: <CreateModal />, label: tr("Create New") })
          }
          style={{ width: "100%" }}
          primary
        >
          <RiAddFill /> {tr("Create")}
        </Button>
        <MenuContainer>
          <MenuLink to="/customers">{tr("Customers")}</MenuLink>
          <MenuLink to="/requests">{tr("Requests")}</MenuLink>
          <MenuLink to="/appointments">{tr("Appointments")}</MenuLink>
          <MenuLink to="/invoices">{tr("Invoices")}</MenuLink>
        </MenuContainer>
        <MenuContainer>
          <MenuLink to="/projects">{tr("Projects")}</MenuLink>
          <MenuLink to="/quotes">{tr("Quotes")}</MenuLink>
        </MenuContainer>
        <MenuContainer>
          <MenuLink to="/">{tr("Analytics")}</MenuLink>
          <MenuLink to="/admin/requests">{tr("Settings")}</MenuLink>
          <MenuLink to="/admin/employees">{tr("Employees")}</MenuLink>
          <MenuLink to="/admin/notifications">{tr("Notification Settings")}</MenuLink>
          <MenuLink to="/admin/templates">{tr("Permission Templates")}</MenuLink>
          <MenuLink to="/settings/questions">{tr("Question Builder")}</MenuLink>
          <MenuLink
            to="/admin/quickbooks"
            onClick={() => void preflightQboConnection()}
          >
            Quickbooks
          </MenuLink>
        </MenuContainer>
      </SideBarSection>
    </SidebarCon>
  );
};
